import { useState } from 'react'
import { motion } from 'framer-motion'
import { Home, Calendar, Users, ChevronRight, AlertTriangle, CheckCircle, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type AppStage = 'Inquiry' | 'Applied' | 'Screening' | 'Approved' | 'Signed'
type RenewalStatus = 'Pending' | 'Sent' | 'Signed' | 'Declined'

type Application = {
  id: string; name: string; unit: string; property: string; applied: string
  income: number; creditScore: number; stage: AppStage; email: string; note: string
}
type Renewal = {
  id: string; tenant: string; unit: string; property: string
  currentRent: number; proposedRent: number; leaseEnd: string
  daysUntil: number; status: RenewalStatus
}
type Vacancy = {
  id: string; unit: string; property: string; beds: number; baths: number
  rent: number; vacantSince: string; daysVacant: number; appCount: number
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const VACANCIES: Vacancy[] = [
  { id: 'v1', unit: 'Unit 2A', property: 'Maple Grove House', beds: 2, baths: 1, rent: 2200, vacantSince: '2026-06-01', daysVacant: 36, appCount: 3 },
]

const APPLICATIONS: Application[] = [
  { id: 'a1', name: 'Tyler Washington', unit: 'Unit 2A – Maple Grove', property: 'Maple Grove', applied: '2026-07-01', income: 82000, creditScore: 728, stage: 'Screening', email: 'tyler.w@email.com', note: 'Strong applicant. Employed 4+ years.' },
  { id: 'a2', name: 'Maya Rodriguez', unit: 'Unit 2A – Maple Grove', property: 'Maple Grove', applied: '2026-07-03', income: 91000, creditScore: 754, stage: 'Applied', email: 'maya.r@email.com', note: 'Best income-to-rent ratio.' },
  { id: 'a3', name: 'Kevin Park', unit: 'Unit 2A – Maple Grove', property: 'Maple Grove', applied: '2026-07-05', income: 68000, creditScore: 695, stage: 'Inquiry', email: 'kevin.p@email.com', note: 'First contact via listing.' },
]

const RENEWALS: Renewal[] = [
  { id: 'rn1', tenant: 'Jordan Blake', unit: 'Unit 4B – Maple Grove', property: 'Maple Grove', currentRent: 2850, proposedRent: 2965, leaseEnd: '2025-12-31', daysUntil: 178, status: 'Pending' },
  { id: 'rn2', tenant: 'Sam Porter', unit: 'Unit 8 – Cedar Ridge', property: 'Cedar Ridge', currentRent: 2400, proposedRent: 2496, leaseEnd: '2026-01-31', daysUntil: 210, status: 'Sent' },
  { id: 'rn3', tenant: 'Avery Kim', unit: 'Unit 12C – Willow Creek', property: 'Willow Creek', currentRent: 3100, proposedRent: 3224, leaseEnd: '2026-02-28', daysUntil: 238, status: 'Pending' },
]

const ALL_STAGES: AppStage[] = ['Inquiry', 'Applied', 'Screening', 'Approved', 'Signed']



const RENEWAL_STYLE: Record<RenewalStatus, string> = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  Sent: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  Signed: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  Declined: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, delta, tone }: { label: string; value: string; delta?: string; tone?: 'up' | 'down' | 'neutral' }) {
  return (
    <Card className="p-3 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-slate-50 leading-none">{value}</p>
      {delta && <p className={cn('mt-1.5 text-xs font-medium',
        tone === 'up' ? 'text-emerald-600 dark:text-emerald-400' :
        tone === 'down' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500'
      )}>{delta}</p>}
    </Card>
  )
}

// ─── Vacancy Pipeline (Kanban) ────────────────────────────────────────────────

function PipelineTab({ onSchedule }: { onSchedule: () => void }) {
  const [apps, setApps] = useState<Application[]>(APPLICATIONS)

  const moveStage = (id: string, dir: 1 | -1) => {
    setApps(prev => prev.map(a => {
      if (a.id !== id) return a
      const idx = ALL_STAGES.indexOf(a.stage)
      const next = ALL_STAGES[Math.max(0, Math.min(ALL_STAGES.length - 1, idx + dir))]
      return { ...a, stage: next }
    }))
  }

  const monthlyLoss = VACANCIES.reduce((s, v) => s + v.rent * Math.round(v.daysVacant / 30), 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Vacant Units" value={String(VACANCIES.length)} delta="1 unit needs tenant" tone="down" />
        <StatCard label="Active Applicants" value={String(apps.length)} delta="Across all stages" tone="up" />
        <StatCard label="Avg Days Vacant" value="36d" delta="Unit 2A on market" tone="down" />
        <StatCard label="Est. Revenue Lost" value={fmt(monthlyLoss)} delta="From vacancy" tone="down" />
      </div>

      {VACANCIES.map(v => (
        <Card key={v.id} className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 shadow-sm">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-700 dark:text-amber-300 shrink-0">
                <Home className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 dark:text-slate-100">{v.unit} — {v.property}</p>
                <p className="text-xs text-slate-500">{v.beds}BR/{v.baths}BA · {fmt(v.rent)}/mo · Vacant {v.daysVacant} days · {v.appCount} applicants</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-full border border-amber-300 bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                {v.appCount} Applicants
              </span>
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={onSchedule}>Schedule Showing</Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {ALL_STAGES.map(stage => {
          const stageApps = apps.filter(a => a.stage === stage)
          return (
            <div key={stage} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60">
                <span className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">{stage}</span>
                <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:text-slate-400">{stageApps.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[140px]">
                {stageApps.map(a => (
                  <motion.div key={a.id} layout className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm">
                    <p className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">{a.name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">{a.unit}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800 rounded-full px-2 py-0.5 font-semibold">
                        {a.creditScore}
                      </span>
                      <span className="text-[10px] text-slate-500">{fmt(a.income)}/yr</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1.5 leading-tight">{a.note}</p>
                    <div className="mt-2.5 flex gap-1">
                      {ALL_STAGES.indexOf(a.stage) > 0 && (
                        <Button size="sm" variant="secondary" className="h-6 px-2 text-[10px] flex-1" onClick={() => moveStage(a.id, -1)}>← Back</Button>
                      )}
                      {ALL_STAGES.indexOf(a.stage) < ALL_STAGES.length - 1 && (
                        <Button size="sm" className="h-6 px-2 text-[10px] flex-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => moveStage(a.id, 1)}>Advance →</Button>
                      )}
                      {a.stage === 'Signed' && (
                        <span className="flex-1 text-center text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Leased
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
                {stageApps.length === 0 && (
                  <div className="flex items-center justify-center h-24 text-[11px] text-slate-400 dark:text-slate-600">No applicants</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Renewals Tab ─────────────────────────────────────────────────────────────

function RenewalsTab({ onNotice }: { onNotice: (message: string) => void }) {
  const [renewals, setRenewals] = useState<Renewal[]>(RENEWALS)
  const totalAddedRevenue = renewals.reduce((s, r) => s + (r.proposedRent - r.currentRent), 0)

  const sendNotice = (id: string) => {
    setRenewals((prev) => prev.map((item) => (item.id === id ? { ...item, status: 'Sent' } : item)))
    onNotice('Renewal notice sent successfully.')
  }

  const markAwaitingComplete = (id: string) => {
    setRenewals((prev) => prev.map((item) => (item.id === id ? { ...item, status: 'Signed' } : item)))
    onNotice('Renewal marked as signed.')
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Expiring (90d)" value={String(renewals.length)} delta="3 leases need action" tone="down" />
        <StatCard label="Notices Sent" value={String(renewals.filter(r => r.status === 'Sent').length)} tone="neutral" />
        <StatCard label="Avg Increase" value="4.0%" delta="Proposed market rate" tone="up" />
        <StatCard label="Added Monthly Revenue" value={fmt(totalAddedRevenue)} delta="If all renewed" tone="up" />
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Upcoming Lease Renewals</CardTitle>
          <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-full px-2.5 py-1 font-semibold">
            <AlertTriangle className="inline h-3 w-3 mr-1" />Action required
          </span>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {renewals.map(r => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={cn('h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold',
                    r.daysUntil < 60 ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                  )}>
                    {r.daysUntil}d
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{r.tenant}</p>
                    <p className="text-xs text-slate-500">{r.unit}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Lease ends {r.leaseEnd}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 ml-auto">
                  <div className="text-right">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Current → Proposed</p>
                    <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
                      {fmt(r.currentRent)} → <span className="text-emerald-700 dark:text-emerald-400">{fmt(r.proposedRent)}</span>
                      <span className="ml-1 text-[11px] text-emerald-600 dark:text-emerald-400">(+{Math.round(((r.proposedRent - r.currentRent) / r.currentRent) * 100)}%)</span>
                    </p>
                  </div>
                  <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', RENEWAL_STYLE[r.status])}>
                    {r.status}
                  </span>
                  {r.status === 'Pending' && (
                    <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => sendNotice(r.id)}>Send Notice</Button>
                  )}
                  {r.status === 'Sent' && (
                    <Button size="sm" variant="secondary" onClick={() => markAwaitingComplete(r.id)}><Clock className="mr-1.5 h-3.5 w-3.5" />Mark Signed</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Renewal Best Practices</p>
              <ul className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-300 space-y-1 list-disc list-inside">
                <li>Send renewal notices 90 days before lease expiration</li>
                <li>Keep increases at 3–5% to maximize retention</li>
                <li>Offer incentives for 2-year lease commitments</li>
                <li>Follow up if no response within 2 weeks</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Showing Schedule Tab ─────────────────────────────────────────────────────

function ShowingsTab({ onSchedule }: { onSchedule: () => void }) {
  const [showings, setShowings] = useState([
    { id: 's1', applicant: 'Tyler Washington', unit: 'Unit 2A – Maple Grove', date: '2026-07-08', time: '2:00 PM', agent: 'A. Rivera', status: 'Confirmed' },
    { id: 's2', applicant: 'Maya Rodriguez', unit: 'Unit 2A – Maple Grove', date: '2026-07-09', time: '11:00 AM', agent: 'A. Rivera', status: 'Confirmed' },
    { id: 's3', applicant: 'Kevin Park', unit: 'Unit 2A – Maple Grove', date: '2026-07-10', time: '3:30 PM', agent: 'J. Brooks', status: 'Pending' },
  ])

  const advanceShowing = (id: string) => {
    setShowings((prev) =>
      prev.map((item) => {
        if (item.id !== id) {
          return item
        }
        return { ...item, status: item.status === 'Pending' ? 'Confirmed' : 'Pending' }
      }),
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Scheduled Showings</CardTitle>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={onSchedule}><Home className="mr-1.5 h-3.5 w-3.5" />Schedule Showing</Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {showings.map(s => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-violet-50 dark:bg-violet-950/30 flex items-center justify-center text-violet-600 dark:text-violet-400 shrink-0">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{s.applicant}</p>
                    <p className="text-xs text-slate-500">{s.unit}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{s.date} at {s.time}</p>
                    <p className="text-[11px] text-slate-500">Agent: {s.agent}</p>
                  </div>
                  <span className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-semibold',
                    s.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800' :
                    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800'
                  )}>{s.status}</span>
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => advanceShowing(s.id)}><ChevronRight className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

type LeasingTab = 'pipeline' | 'renewals' | 'showings'

export default function LeasingModule() {
  const [activeTab, setActiveTab] = useState<LeasingTab>('pipeline')
  const [notice, setNotice] = useState<string | null>(null)

  const tabs: { id: LeasingTab; label: string; icon: React.ElementType }[] = [
    { id: 'pipeline', label: 'Vacancy Pipeline', icon: Home },
    { id: 'renewals', label: 'Lease Renewals', icon: Calendar },
    { id: 'showings', label: 'Showings', icon: Users },
  ]

  return (
    <div className="space-y-4">
      {notice && (
        <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <CardContent className="py-2 text-sm text-emerald-800 dark:text-emerald-300">{notice}</CardContent>
        </Card>
      )}
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Leasing</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">Manage vacancies, applications, renewals, and showings</p>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
        {tabs.map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-all',
                activeTab === t.id
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-white/60 dark:hover:bg-slate-700'
              )}>
              <Icon className="h-4 w-4" />{t.label}
            </button>
          )
        })}
      </div>

      <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
        {activeTab === 'pipeline' && <PipelineTab onSchedule={() => {
          setActiveTab('showings')
          setNotice('Showing flow opened for scheduling.')
        }} />}
        {activeTab === 'renewals' && <RenewalsTab onNotice={setNotice} />}
        {activeTab === 'showings' && <ShowingsTab onSchedule={() => setNotice('New showing request queued.')} />}
      </motion.div>
    </div>
  )
}
